import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  HomeIcon,
  UserPlusIcon,
  ChartBarSquareIcon,
  BellIcon,
  ListBulletIcon,
  StarIcon,
  CurrencyDollarIcon,
  UsersIcon,
  PhoneIcon,
  MapPinIcon,
  ChatBubbleLeftRightIcon,
  CubeIcon,
  DevicePhoneMobileIcon,
  BoltIcon,
  BookOpenIcon,
  PhotoIcon,
  MegaphoneIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  MagnifyingGlassIcon,
  ChevronRightIcon,
  ShoppingCartIcon,
} from "@heroicons/react/24/outline";
import { ADMIN_ROUTES, getAdminSectionLabel } from "../config/adminRoutes";
import { getServerMode } from "../config/connect";
import ThemeSwitch from "./ThemeSwitch";
import ServerModeToggle from "./ServerModeToggle";
import logo from "../logo.svg";

const ICON_MAP = {
  home: HomeIcon,
  user: UserPlusIcon,
  chart: ChartBarSquareIcon,
  bell: BellIcon,
  list: ListBulletIcon,
  star: StarIcon,
  wallet: CurrencyDollarIcon,
  users: UsersIcon,
  phone: PhoneIcon,
  map: MapPinIcon,
  message: ChatBubbleLeftRightIcon,
  package: CubeIcon,
  smartphone: DevicePhoneMobileIcon,
  activity: BoltIcon,
  book: BookOpenIcon,
  image: PhotoIcon,
  megaphone: MegaphoneIcon,
  cart: ShoppingCartIcon,
};

const GROUP_META = {
  overview: { label: "Overview" },
  users: { label: "Users" },
  chat: { label: "Chat" },
  marketing: { label: "Marketing" },
  finance: { label: "Finance" },
  system: { label: "System" },
};

function decodeJwtPayload(token) {
  try {
    const p = token.split(".")[1];
    return JSON.parse(atob(p.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return null;
  }
}

function AdminLayout({ onLogout, activeTab, setActiveTab }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [userInfo, setUserInfo] = useState(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const t = localStorage.getItem("google_id_token");
    if (t) setUserInfo(decodeJwtPayload(t));
  }, []);

  // Ctrl/⌘+K — бөлімді іздеуге фокус
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSidebarOpen(true);
        searchRef.current?.focus();
        searchRef.current?.select();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? ADMIN_ROUTES.filter(
          (r) =>
            r.label.toLowerCase().includes(q) || r.path.toLowerCase().includes(q)
        )
      : ADMIN_ROUTES;
    const out = {};
    filtered.forEach((r) => {
      const g = r.group || "system";
      if (!out[g]) out[g] = [];
      out[g].push(r);
    });
    // Preserve group order
    const orderedKeys = Object.keys(GROUP_META).filter((k) => out[k]);
    return orderedKeys.map((k) => ({ key: k, meta: GROUP_META[k], routes: out[k] }));
  }, [search]);

  const activeRoute = ADMIN_ROUTES.find((r) => r.path === activeTab);
  const ActiveComponent = activeRoute?.Component;
  const breadcrumbLabel = getAdminSectionLabel(activeTab);
  const isOverview = activeTab === "overview";
  const ActiveIcon = ICON_MAP[activeRoute?.icon] || CubeIcon;
  const isProd = getServerMode() === "prod";

  const handleLogout = () => {
    if (window.confirm("Панельден шығасыз ба?")) onLogout();
  };

  const closeSidebar = () => setSidebarOpen(false);

  const navigate = (path) => {
    setActiveTab(path);
    closeSidebar();
  };

  const userName = userInfo?.name || userInfo?.email?.split("@")[0] || "Admin";
  const userEmail = userInfo?.email || "";
  const userPic = userInfo?.picture;

  return (
    <div className="h-screen overflow-hidden flex bg-bg-base">
      {/* Mobile overlay */}
      <div
        role="presentation"
        className={`fixed inset-0 z-40 bg-black/60 transition-opacity lg:hidden ${
          sidebarOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={closeSidebar}
        aria-hidden="true"
      />

      {/* Sidebar */}
      <aside
        id="admin-sidebar"
        aria-label="Навигация"
        className={`fixed top-0 left-0 z-50 h-full w-64 flex flex-col text-left dark-sidebar border-r border-border-primary transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Brand — биіктігі тақырып жолағымен бірдей (h-14) */}
        <div className="flex h-14 shrink-0 items-center justify-between gap-2 border-b border-border-primary px-4">
          <button
            type="button"
            onClick={() => navigate("overview")}
            className="flex min-w-0 items-center gap-2.5 rounded-lg text-left"
          >
            <img src={logo} alt="" className="h-8 w-8 shrink-0 rounded-lg bg-bg-tertiary p-1" />
            <span className="min-w-0">
              <span className="block truncate text-[15px] font-bold leading-tight text-text-primary">Agroland</span>
              <span className="block text-[11px] leading-tight text-text-muted">Панель администратора</span>
            </span>
          </button>
          <button
            type="button"
            onClick={closeSidebar}
            aria-label="Закрыть меню"
            className="rounded-lg p-1.5 text-text-secondary hover:bg-bg-tertiary hover:text-text-primary lg:hidden"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Search */}
        <div className="shrink-0 px-3 pb-2 pt-3">
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && grouped[0]?.routes[0]) {
                  navigate(grouped[0].routes[0].path);
                  setSearch("");
                  e.currentTarget.blur();
                } else if (e.key === "Escape") {
                  setSearch("");
                  e.currentTarget.blur();
                }
              }}
              placeholder="Бөлімді іздеу..."
              aria-label="Бөлімді іздеу"
              className="h-9 w-full rounded-lg border border-border-primary bg-bg-primary pl-9 pr-12 text-sm text-text-primary placeholder:text-text-muted outline-none transition-colors focus:border-[#4F45E5] focus:ring-2 focus:ring-[#4F45E5]/25"
            />
            {search ? (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Тазалау"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-text-muted hover:text-text-primary"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            ) : (
              <kbd className="pointer-events-none absolute right-2 top-1/2 hidden -translate-y-1/2 rounded border border-border-primary px-1.5 py-px font-sans text-[10px] text-text-muted lg:block">
                Ctrl K
              </kbd>
            )}
          </div>
        </div>

        {/* Nav groups */}
        <nav
          className="flex-1 overflow-y-auto px-3 pb-3 [scrollbar-color:var(--border-primary)_transparent] [scrollbar-width:thin]"
          aria-label="Разделы"
        >
          {grouped.length === 0 && (
            <div className="px-3 py-8 text-center text-sm text-text-muted">
              Ешнәрсе табылмады
            </div>
          )}
          {grouped.map((g) => (
            <div key={g.key} className="pt-3 first:pt-1">
              <div className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                {g.meta.label}
              </div>
              <ul className="space-y-0.5">
                {g.routes.map((r) => {
                  const Icon = ICON_MAP[r.icon] || CubeIcon;
                  const active = activeTab === r.path;
                  return (
                    <li key={r.id}>
                      <button
                        type="button"
                        onClick={() => {
                          navigate(r.path);
                          setSearch("");
                        }}
                        aria-current={active ? "page" : undefined}
                        title={r.label}
                        className={`relative flex h-9 w-full items-center gap-3 rounded-lg px-3 text-left text-sm transition-colors ${
                          active
                            ? "bg-[#4F45E5]/15 font-medium text-text-primary"
                            : "text-text-secondary hover:bg-bg-tertiary hover:text-text-primary"
                        }`}
                      >
                        {active && (
                          <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r bg-accent-grad" aria-hidden="true" />
                        )}
                        <Icon className={`h-[18px] w-[18px] shrink-0 ${active ? "text-[#7C3AED]" : ""}`} />
                        <span className="truncate">{r.label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        {/* User + logout — бір жолда */}
        <div className="shrink-0 border-t border-border-primary p-3">
          <div className="flex items-center gap-3 rounded-lg px-2 py-1.5">
            {userPic ? (
              <img
                src={userPic}
                alt=""
                referrerPolicy="no-referrer"
                className="h-9 w-9 shrink-0 rounded-full border border-border-primary"
              />
            ) : (
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-grad text-sm font-semibold text-white">
                {userName?.[0]?.toUpperCase() || "A"}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-text-primary">{userName}</div>
              <div className="truncate text-xs text-text-muted" title={userEmail}>{userEmail}</div>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              title="Шығу"
              aria-label="Шығу"
              className="shrink-0 rounded-lg p-2 text-text-muted transition-colors hover:bg-rose-500/10 hover:text-rose-500"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main — айналу тек осы жерде, сайдбар орнында қалады */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        {/* Top bar */}
        <header
          className={`relative h-14 flex items-center gap-3 px-4 lg:px-6 border-b border-border-primary shrink-0 bg-bg-card ${
            isProd ? "shadow-[inset_0_2px_0_0_#e11d48]" : ""
          }`}
        >
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="p-2 -ml-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-bg-tertiary lg:hidden"
            aria-label="Открыть меню"
          >
            <Bars3Icon className="w-5 h-5" />
          </button>
          <nav aria-label="Breadcrumb" className="flex min-w-0 items-center text-sm">
            <button
              type="button"
              onClick={() => navigate("overview")}
              className={`${isOverview ? "inline-flex" : "hidden sm:inline-flex"} items-center gap-1.5 rounded-md px-1.5 py-1 text-text-secondary hover:bg-bg-tertiary hover:text-text-primary transition-colors`}
            >
              <HomeIcon className="w-4 h-4" />
              Admin
            </button>
            {!isOverview && (
              <>
                <ChevronRightIcon className="hidden sm:block mx-1 w-3.5 h-3.5 shrink-0 text-text-muted" aria-hidden="true" />
                <span className="flex min-w-0 items-center gap-2 px-1.5 font-semibold text-text-primary" aria-current="page">
                  <ActiveIcon className="w-4 h-4 shrink-0 text-[#7C3AED]" />
                  <span className="truncate">{breadcrumbLabel}</span>
                </span>
              </>
            )}
          </nav>
          <div className="ml-auto flex shrink-0 items-center gap-2">
            <ServerModeToggle inline />
            <span className="mx-1 hidden h-6 w-px bg-border-primary sm:block" aria-hidden="true" />
            <ThemeSwitch compact />
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 min-h-0 p-4 lg:p-6 overflow-auto">
          {ActiveComponent ? (
            isOverview ? (
              <ActiveComponent userEmail={userEmail} onNavigate={navigate} />
            ) : (
              <ActiveComponent />
            )
          ) : null}
        </main>
      </div>
    </div>
  );
}

export default AdminLayout;
